import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import SendIcon from "@mui/icons-material/Send";
import EditDocumentIcon from "@mui/icons-material/DriveFileRenameOutline";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import CloseIcon from "@mui/icons-material/Close";
import { useAuthenticator } from "@aws-amplify/ui-react";

const SOURCE_LINK_PREFIX = "https://archie.beeri.org.il";
const FALLBACK_QUESTIONS = [
  "מי זכאי לתור בשיכון 2020?",
  "אילו ימים מערכת החינוך סגורה?",
  "באילו גנים נערכות הערכות?",
  "איפה פורסם הגרנוליטון בראש השנה?",
  "מה עדכונים בהתארגנות מערכת החינוך?",
];

export default function ChatWidget() {
  const { route, user } = useAuthenticator((context) => [
    context.route,
    context.user,
  ]);
  const isAuthenticated = route === "authenticated" && Boolean(user);

  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] =
    useState(FALLBACK_QUESTIONS);
  // Messages are objects: { id: string, type: 'question' | 'answer' | 'pending', content?: string, sources?: [] }
  const [messages, setMessages] = useState([]);
  const messageIdRef = useRef(0);

  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (isAuthenticated) return;
    setOpen(false);
    setText("");
    setSending(false);
    setMessages([]);
  }, [isAuthenticated]);

  const normalizeSourceTitle = useCallback((value) => {
    return String(value || "")
      .replace(/^\s*\d+\s*:\s*/, "")
      .replace(/^\s*(?:כתבה|source|מקור)\s*:\s*/i, "")
      .replace(/\s+/g, " ")
      .replace(/[;.,\s]+$/, "")
      .trim();
  }, []);

  const loadQuestions = useCallback(async (signal) => {
    try {
      const response = await fetch(
        `https://${process.env.REACT_APP_API_ENDPOINT}/questions`,
        {
          headers: new Headers({
            "ngrok-skip-browser-warning": "69420",
          }),
        },
      );

      const data = await response.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.questions)
          ? data.questions
          : Array.isArray(data?.results)
            ? data.results
            : [];

      const normalized = list
        .map((item) =>
          typeof item === "string"
            ? item.trim()
            : item?.question || item?.title || item?.text || "",
        )
        .filter(Boolean)
        .slice(0, 5);

      if (normalized.length > 0) {
        setSuggestedQuestions(normalized);
      } else {
        setSuggestedQuestions(FALLBACK_QUESTIONS);
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        setSuggestedQuestions(FALLBACK_QUESTIONS);
      }
    }
  }, []);

  const refreshQuestions = useCallback(() => {
    const controller = new AbortController();
    loadQuestions(controller.signal);
    return () => controller.abort();
  }, [loadQuestions]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const controller = new AbortController();

    loadQuestions(controller.signal);
    return () => controller.abort();
  }, [isAuthenticated, loadQuestions]);

  const send = useCallback(
    async (manualText) => {
      if (!isAuthenticated) return;
      const content = (
        typeof manualText === "string" ? manualText : text
      ).trim();
      if (!content || sending) return;

      // Clean any potential HTML tags if they somehow entered the state
      const cleanContent = content.replace(/<\/?([^>]+)(>|$)/g, "");

      // Prepare conversation history for the backend (last 3 turns)
      const history = messages
        .filter((m) => m.type === "question" || m.type === "answer")
        .slice(-6)
        .map((m) => ({
          role: m.type === "question" ? "user" : "assistant",
          content: m.content.replace(/<\/?([^>]+)(>|$)/g, ""),
        }));

      const questionId = `q-${messageIdRef.current++}`;
      const pendingId = `p-${messageIdRef.current++}`;

      setMessages((m) => [
        ...m,
        { id: questionId, type: "question", content: cleanContent },
        { id: pendingId, type: "pending" },
      ]);
      setText("");
      setSending(true);

      try {
        const r = await fetch(
          `https://${process.env.REACT_APP_API_ENDPOINT}/chat`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              prompt: content,
              history: history,
            }),
          },
        );
        const data = await r.json().catch(() => ({}));
        const reply =
          typeof data?.answer === "string" ? data.answer : "(no reply)";
        const sources = buildSources(data);
        setMessages((m) =>
          m.map((message) =>
            message.id === pendingId
              ? { id: pendingId, type: "answer", content: reply, sources }
              : message,
          ),
        );
      } catch (e) {
        setMessages((m) =>
          m.map((message) =>
            message.id === pendingId
              ? {
                  id: pendingId,
                  type: "answer",
                  content: "שגיאה בשליחה. בדקו חיבור או את ה-API.",
                }
              : message,
          ),
        );
      } finally {
        setSending(false);
      }
    },
    [isAuthenticated, text, sending, messages],
  );

  if (!isAuthenticated) return null;

  return (
    <Wrap dir="rtl" $open={open}>
      {!open && (
        <Fab aria-label="פתיחת צ'אט" onClick={() => setOpen(true)}>
          💬
        </Fab>
      )}

      <Drawer
        $open={open}
        role="dialog"
        aria-modal="true"
        aria-label="חלון צ'אט"
      >
        <Head>
          <Title>צ׳אט ארכיון בארי (פיילוט)</Title>
          <HeadBtns>
            <Btn onClick={() => setMessages([])} title="שיחה חדשה">
              <EditDocumentIcon fontSize="small" />
            </Btn>
            <Btn onClick={refreshQuestions} title="רענון שאלות">
              <ShuffleIcon fontSize="small" />
            </Btn>
            <Btn onClick={() => setOpen(false)} title="סגור">
              <CloseIcon fontSize="small" />
            </Btn>
          </HeadBtns>
        </Head>

        <Msgs ref={listRef}>
          {messages.length === 0 && (
            <Empty>
              <h4>שלום! 👋</h4>
              <p>כתבו הודעה והקישו Enter לשליחה.</p>
            </Empty>
          )}
          {messages.map((m) => (
            <Bubble
              key={m.id}
              $question={m.type === "question"}
              $pending={m.type === "pending"}
            >
              {m.type === "pending" ? (
                <PendingState>
                  <Dots aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </Dots>
                </PendingState>
              ) : m.type === "question" ? (
                <pre>{m.content}</pre>
              ) : (
                <MessageText>
                  {renderAnswer(m.content, m.sources, normalizeSourceTitle)}
                </MessageText>
              )}
            </Bubble>
          ))}
        </Msgs>

        <Suggestions>
          {suggestedQuestions.map((q) => (
            <SuggestionChip key={q} onClick={() => send(q)}>
              {q}
            </SuggestionChip>
          ))}
        </Suggestions>

        <Bar
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <Composer>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="כתבו הודעה…"
              disabled={sending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <SendButton
              type="submit"
              disabled={sending || !text.trim()}
              aria-label="שליחה"
              title="שליחה"
            >
              <SendIcon fontSize="small" />
            </SendButton>
          </Composer>
        </Bar>
      </Drawer>
    </Wrap>
  );
}

function buildSources(data) {
  const sources = Array.isArray(data?.sources)
    ? data.sources
    : Array.isArray(data?.contextPassages)
      ? data.contextPassages
      : Array.isArray(data?.retrieval?.qdrant)
        ? data.retrieval.qdrant
        : [];

  return sources
    .map((entry, index) => {
      const payload = entry?.payload ?? entry;
      const docId = payload?.docId ?? payload?.id;
      const wikiPageId = payload?.wikiPageId;
      if (!docId && !wikiPageId) return null;
      return {
        number: entry?.number ?? index + 1,
        docId,
        wikiPageId,
        title: payload?.title || "",
      };
    })
    .filter(Boolean);
}

function renderAnswer(content, sources = [], normalizeSourceTitle) {
  const sourceMap = new Map(sources.map((source) => [source.number, source]));
  const titleMap = new Map(
    sources.map((source) => [normalizeSourceTitle(source.title), source]),
  );
  const parts = [];
  const sourcePattern = /\[([^\]]+)\]/g;
  let lastIndex = 0;
  let match;

  while ((match = sourcePattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }

    const rawLabel = match[1];
    const sourceNumberMatch = rawLabel.match(/^(?:source|מקור)\s*(\d+)$/i);
    const numericMatch = rawLabel.match(/^(\d+)\s*:\s*(.+)$/);
    const citationTitle = normalizeSourceTitle(
      numericMatch ? numericMatch[2] : rawLabel,
    );
    const source =
      sourceMap.get(Number(sourceNumberMatch?.[1] || numericMatch?.[1])) ||
      titleMap.get(citationTitle);
    const docId = source?.docId;
    const wikiPageId = source?.wikiPageId;
    const label = `[${rawLabel}]`;

    if (wikiPageId) {
      parts.push(
        <SourceLink
          key={`${source?.number ?? rawLabel}-${match.index}`}
          href={`https://wikibbutz.beeri.org.il/index.php?curid=${wikiPageId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </SourceLink>,
      );
    } else if (docId) {
      parts.push(
        <SourceLink
          key={`${source?.number ?? rawLabel}-${match.index}`}
          href={`${SOURCE_LINK_PREFIX}/${docId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </SourceLink>,
      );
    } else {
      parts.push(
        <SourceFallback key={`${rawLabel}-${match.index}`}>
          {label}
        </SourceFallback>,
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts.length > 0 ? parts : content;
}

const Wrap = styled.div`
  position: fixed;
  inset: auto 0 0 auto;
  z-index: ${({ $open }) => ($open ? 101 : 1)};
`;

const Fab = styled.button`
  position: fixed;
  bottom: 100px;
  right: 40px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  font-size: 22px;
  display: grid;
  place-items: center;
  color: #fff;
  background: #353535;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  z-index: 2;
`;

const slideIn = keyframes`
  from { transform: translateX(110%); opacity: 0.5; }
  to { transform: translateX(0); opacity: 1; }
`;
const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(110%); opacity: 0.4; }
`;

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  z-index: ${({ $open }) => ($open ? 101 : 1)};
  width: min(420px, 100vw);
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  display: grid;
  grid-template-rows: auto 1fr auto auto;
  background: #fff;
  color: #000;
  border-left: 1px solid #ccc;
  box-sizing: border-box;
  overflow: hidden;
  ${({ $open }) =>
    $open
      ? css`
          animation: ${slideIn} 180ms ease forwards;
        `
      : css`
          animation: ${slideOut} 150ms ease forwards;
        `}

  @media (max-width: 640px) {
    left: 0;
    width: 100vw;
    border-left: 0;
    border-right: 0;
  }
`;

const Head = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #ccc;
`;
const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  font-family: Rubik, system-ui, sans-serif;
`;
const HeadBtns = styled.div`
  display: inline-flex;
  gap: 6px;
`;
const Btn = styled.button`
  background: transparent;
  color: #000;
  border: none; /* Removed border to match the thin-lined aesthetic of the 'x' character */
  border-radius: 10px;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Msgs = styled.div`
  overflow: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;
const Empty = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 24px 12px 8px;
  text-align: center;
  color: #333;
  h4,
  p {
    margin: 0;
  }
  p {
    line-height: 1.45;
  }
`;

const Bubble = styled.div`
  align-self: ${({ $question }) => ($question ? "flex-start" : "flex-end")};
  max-width: ${({ $question }) => ($question ? "80%" : "100%")};
  border-radius: 14px;
  padding: 10px 12px;
  line-height: 1.45;
  font-family: Rubik, system-ui, sans-serif;
  font-size: 14px;
  word-break: break-word;
  overflow-wrap: anywhere;
  ${({ $question }) =>
    $question
      ? css`
          background: #4f7ef7;
          color: #fff;
        `
      : css`
          background: #f0f0f0;
          color: #000;
        `}
  ${({ $pending }) =>
    $pending &&
    css`
      background: #e9e9e9;
      color: #555;
    `}
  pre {
    margin: 0;
    font-family: inherit;
    white-space: pre-wrap; /* allow wrapping of long content */
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`;

const MessageText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const PendingState = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const dots = keyframes`
  0%, 20% { opacity: 0.2; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
  60%, 100% { opacity: 0.2; transform: translateY(0); }
`;

const Dots = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;

  span {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #8c8c8c;
    animation: ${dots} 1s infinite ease-in-out;
  }

  span:nth-child(2) {
    animation-delay: 0.15s;
  }

  span:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

const SourceLink = styled.a`
  color: #0b57d0;
  font-weight: 400;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const SourceFallback = styled.span`
  color: #0b57d0;
  font-weight: 400;
`;

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 12px 10px;
  background: #fff;
`;

const SuggestionChip = styled.button`
  background: #f0f4ff;
  border: 1px solid #d0deff;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  color: #0b57d0;
  font-family: inherit;
  &:hover {
    background: #e1e9ff;
  }
`;

const Bar = styled.form`
  display: flex;
  align-items: center;
  padding: 10px 12px 12px;
  border-top: 1px solid #ccc;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
`;
const Composer = styled.div`
  position: relative;
  width: 100%;
`;
const Input = styled.textarea`
  resize: none;
  min-height: 44px;
  max-height: 140px;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px 10px 46px;
  border-radius: 12px;
  border: 1px solid #ccc;
  background: #fff;
  color: #000;
  font-family: Rubik, system-ui, sans-serif;
`;
const SendButton = styled.button`
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: #4f7ef7;
  color: #fff;
  svg {
    display: block;
    width: 18px;
    height: 18px;
    transform: scaleX(-1) translateY(1px);
    transform-origin: center;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
